import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus, MapPin, Calculator, TrendingUp, TrendingDown,
  Trash2, X, CheckCircle2, AlertTriangle, Loader2,
  Database, Fingerprint, ShieldCheck, Save, RefreshCw,
  BarChart3, Target, FileText, Info,
} from 'lucide-react';

import AmostrasService, {
  fetchAmostrasReais,
  homogeneizar,
  sanearAmostras,
} from '../../services/AmostrasService';
import { laudoApi } from '../../services/laudoApi';
import { LIMITES } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: ImovelParadigma_Amostras
 * ------------------------------------------------------------
 * Coleta de amostras de mercado (NBR 14653-2).
 *   ✔ Busca amostras reais via GPS (AmostrasService)
 *   ✔ Cadastro manual com schema completo
 *   ✔ Homogeneização V_h = V_b × (1 + i)
 *   ✔ Saneamento estatístico (outliers ±2σ)
 *   ✔ Persistência via laudoApi
 * VERSÃO: 2.0.0
 * ============================================================
 */

const ORIGENS = [
  'ESCRITURA',
  'CONTRATO',
  'MATRICULA',
  'ANUNCIO',
  'BANCO_DADOS',
  'CAPTURA_TELA',
];

const FONTES_DATA = ['OCR', 'DECLARADA', 'CAPTURA'];

/* =========================
   HELPERS
========================= */

const formatBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(Number(v) || 0);

const formatPct = (v) => {
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const hoje = () => new Date().toISOString().slice(0, 10);

/* =========================
   COMPONENTE
========================= */

export default function ImovelParadigma_Amostras({
  reportNumber = 'REPORT-2026-MH-001',
  peritoAtivo = 'MONTE_HERMOM_USER',
  coordenadas = null,
  onBack,
  onComplete,
}) {
  const [amostras, setAmostras] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [raioKm, setRaioKm] = useState(1.5);

  /* ---------- estado do formulário manual ---------- */
  const [form, setForm] = useState({
    identificacao: '',
    valor_base: '',
    area_referencia: '',
    fator_ajuste: '0.00',
    data_amostra: hoje(),
    fonte_data: 'DECLARADA',
    origem_tipo: 'ANUNCIO',
    origem_url: '',
    justificativa: '',
  });

  /* =========================
     BUSCA GPS
  ========================= */

  const buscarPorGPS = async () => {
    setErro(null);
    setAviso(null);

    if (!coordenadas?.lat || !coordenadas?.lng) {
      setErro(
        'Coordenadas do imóvel não informadas. Defina o PIN no mapa antes de buscar amostras.'
      );
      return;
    }

    setCarregando(true);
    try {
      const resultado = await fetchAmostrasReais(
        Number(coordenadas.lat),
        Number(coordenadas.lng),
        raioKm,
        { reportNumber, perito: peritoAtivo }
      );

      const homogeneizadas = homogeneizar(resultado.amostras);
      const saneadas = sanearAmostras(homogeneizadas, 2);

      setAmostras(prev => [...prev, ...saneadas.amostrasSaneadas]);

      if (resultado.avisos?.length) {
        setAviso(resultado.avisos.join(' · '));
      }

      if (saneadas.descartadas.length > 0) {
        setAviso(prev => [
          prev,
          `${saneadas.descartadas.length} outlier(s) removido(s) no saneamento estatístico.`,
        ].filter(Boolean).join(' · '));
      }
    } catch (err) {
      setErro(err.message || 'Falha na busca de amostras');
    } finally {
      setCarregando(false);
    }
  };

  /* =========================
     CADASTRO MANUAL
  ========================= */

  const resetarForm = () => setForm({
    identificacao: '',
    valor_base: '',
    area_referencia: '',
    fator_ajuste: '0.00',
    data_amostra: hoje(),
    fonte_data: 'DECLARADA',
    origem_tipo: 'ANUNCIO',
    origem_url: '',
    justificativa: '',
  });

  const adicionarManual = () => {
    setErro(null);

    // Validação
    if (!form.identificacao.trim()) {
      setErro('Identificação da amostra é obrigatória.');
      return;
    }
    const valor = parseFloat(String(form.valor_base).replace(/[^\d,-]/g, '').replace(',', '.'));
    const area = parseFloat(String(form.area_referencia).replace(',', '.'));
    const fator = parseFloat(form.fator_ajuste) || 0;

    if (!Number.isFinite(valor) || valor <= 0) {
      setErro('Valor base deve ser um número positivo.');
      return;
    }
    if (!Number.isFinite(area) || area <= 0) {
      setErro('Área de referência deve ser um número positivo.');
      return;
    }
    if (fator < -1 || fator > 1) {
      setErro('Fator de ajuste deve estar entre -1.00 e 1.00.');
      return;
    }
    if (
      form.origem_tipo !== 'CAPTURA_TELA' &&
      form.origem_tipo !== 'BANCO_DADOS' &&
      !form.justificativa.trim()
    ) {
      setErro(
        'Justificativa é obrigatória para origem não documental (NBR 14653-2).'
      );
      return;
    }

    const valorHomogeneizado = +(valor * (1 + fator)).toFixed(2);

    const nova = {
      id: `M-${Date.now().toString(36).toUpperCase()}`,
      identificacao: form.identificacao.trim(),
      valor_base: valor,
      area_referencia: area,
      fator_ajuste: fator,
      valor_homogeneizado: valorHomogeneizado,
      valorUnitario: +(valorHomogeneizado / area).toFixed(2),
      data_amostra: form.data_amostra,
      fonte_data: form.fonte_data,
      origem: {
        tipo: form.origem_tipo,
        documento_id: null,
        url_referencia: form.origem_url || null,
      },
      justificativa: form.justificativa.trim() || null,
      imutavel: true,
      captura_normatizada: null,
      auditoria: {
        criado_em: new Date().toISOString(),
        criado_por: peritoAtivo,
        versao_schema: '1.0.0',
      },
    };

    setAmostras(prev => [...prev, nova]);
    resetarForm();
    setMostrarModal(false);
  };

  /* =========================
     REMOÇÃO
  ========================= */

  const remover = (id) => {
    setAmostras(prev => prev.filter(a => a.id !== id));
  };

  /* =========================
     ESTATÍSTICAS
  ========================= */

  const estatisticas = useMemo(() => {
    if (amostras.length === 0) {
      return {
        total: 0,
        mediaValor: 0,
        mediaUnitario: 0,
        desvioUnitario: 0,
        menor: 0,
        maior: 0,
      };
    }

    const unitarios = amostras
      .map(a => a.valorUnitario)
      .filter(v => Number.isFinite(v) && v > 0);

    const mediaValor = amostras.reduce((s, a) => s + a.valor_homogeneizado, 0) / amostras.length;
    const mediaUnitario = unitarios.length > 0
      ? unitarios.reduce((s, v) => s + v, 0) / unitarios.length
      : 0;

    const variancia = unitarios.length > 0
      ? unitarios.reduce((s, v) => s + Math.pow(v - mediaUnitario, 2), 0) / unitarios.length
      : 0;

    return {
      total: amostras.length,
      mediaValor: +mediaValor.toFixed(2),
      mediaUnitario: +mediaUnitario.toFixed(2),
      desvioUnitario: +Math.sqrt(variancia).toFixed(2),
      menor: Math.min(...amostras.map(a => a.valor_homogeneizado)),
      maior: Math.max(...amostras.map(a => a.valor_homogeneizado)),
    };
  }, [amostras]);

  /* =========================
     PERSISTÊNCIA
  ========================= */

  const salvarEAvancar = async () => {
    if (amostras.length === 0) {
      setErro('Nenhuma amostra coletada. Adicione ao menos uma antes de prosseguir.');
      return;
    }

    setSalvando(true);
    setErro(null);

    try {
      await laudoApi.salvarModulo({
        report_id: reportNumber,
        perito: peritoAtivo,
        modulo: 'AMOSTRAS_MERCADO',
        timestamp: new Date().toISOString(),
        total_amostras: amostras.length,
        centro_busca: coordenadas,
        raio_km: raioKm,
        estatisticas,
        amostras,
      });

      if (typeof onComplete === 'function') {
        onComplete({ amostras, estatisticas });
      }
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — amostras salvas localmente e serão sincronizadas.');
        setTimeout(() => {
          if (typeof onComplete === 'function') {
            onComplete({ amostras, estatisticas });
          }
        }, 1500);
      } else {
        setErro(`Falha ao salvar: ${err.message}`);
      }
    } finally {
      setSalvando(false);
    }
  };

  const limparTudo = () => {
    if (amostras.length === 0) return;
    if (window.confirm(`Remover todas as ${amostras.length} amostras?`)) {
      setAmostras([]);
    }
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="w-full min-h-screen bg-slate-950 text-slate-200 p-8 lg:p-12 font-black italic uppercase">

      {/* WATERMARK */}
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.02] text-[150px] z-0 text-white select-none">
        MONTE HERMOM
      </div>

      <div className="max-w-6xl mx-auto relative z-10 space-y-8">

        {/* HEADER */}
        <header className="bg-slate-900 rounded-[3rem] p-10 border border-white/5 shadow-2xl flex flex-wrap justify-between items-center gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-amber-400">
              <BarChart3 size={24} />
              <span className="text-[10px] tracking-[0.5em] text-slate-500">
                MERCADO IMOBILIÁRIO
              </span>
            </div>
            <h1 className="text-3xl text-white tracking-tight leading-none">
              Amostras de <span className="text-amber-400">Mercado</span>
            </h1>
            <p className="text-[10px] text-slate-500 tracking-[0.3em]">
              NBR 14653-2:2011 · Comparação Direta de Dados
            </p>
          </div>

          <div className="bg-slate-950 px-8 py-4 rounded-[2rem] border border-white/5 flex items-center gap-4">
            <Database size={20} className="text-amber-400" />
            <div className="text-right">
              <span className="block text-[9px] text-slate-500 tracking-widest">
                RELATÓRIO
              </span>
              <span className="text-white text-[16px]">{reportNumber}</span>
            </div>
          </div>
        </header>

        {/* ALERTAS */}
        {erro && (
          <div className="bg-red-950/50 border-2 border-red-500/30 rounded-[2rem] p-6 flex items-start gap-4 whitespace-pre-line">
            <AlertTriangle size={22} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-red-200 text-[11px] tracking-widest leading-relaxed normal-case">
              {erro}
            </span>
          </div>
        )}

        {aviso && !erro && (
          <div className="bg-amber-950/30 border-2 border-amber-500/20 rounded-[2rem] p-6 flex items-start gap-4">
            <Info size={22} className="text-amber-400 shrink-0 mt-0.5" />
            <span className="text-amber-200 text-[11px] tracking-widest leading-relaxed normal-case">
              {aviso}
            </span>
          </div>
        )}

        {/* BARRA DE AÇÕES */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* BUSCA GPS */}
          <div className="lg:col-span-8 bg-slate-900 rounded-[2.5rem] p-6 border border-white/5 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <MapPin size={20} className="text-amber-400" />
              <div className="min-w-0">
                <span className="block text-[9px] text-slate-500 tracking-widest">
                  COORDENADAS DO IMÓVEL
                </span>
                <span className="text-white text-[12px] font-mono normal-case">
                  {coordenadas?.lat && coordenadas?.lng
                    ? `${coordenadas.lat}, ${coordenadas.lng}`
                    : 'Não definidas no mapa'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[9px] text-slate-500 tracking-widest">
                RAIO (km)
              </span>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="10"
                value={raioKm}
                onChange={(e) => setRaioKm(parseFloat(e.target.value) || 1.5)}
                className="w-20 h-10 bg-slate-950 border border-white/10 rounded-xl px-3 text-center text-white text-[13px] font-mono outline-none focus:border-amber-400 transition-all"
              />
            </div>

            <button
              type="button"
              onClick={buscarPorGPS}
              disabled={carregando || !coordenadas?.lat}
              className="bg-amber-400 text-slate-950 px-6 py-3 rounded-2xl text-[11px] tracking-widest flex items-center gap-3 hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {carregando
                ? <><Loader2 size={16} className="animate-spin" /> BUSCANDO...</>
                : <><RefreshCw size={16} /> BUSCAR AMOSTRAS</>}
            </button>
          </div>

          {/* NOVA MANUAL */}
          <button
            type="button"
            onClick={() => setMostrarModal(true)}
            className="lg:col-span-4 bg-white text-slate-950 rounded-[2.5rem] p-6 border border-white/5 flex items-center justify-center gap-3 hover:bg-amber-400 transition-all"
          >
            <Plus size={20} />
            <span className="text-[12px] tracking-widest">NOVA AMOSTRA MANUAL</span>
          </button>
        </section>

        {/* ESTATÍSTICAS */}
        {amostras.length > 0 && (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total de Amostras"
              valor={estatisticas.total}
              icon={Target}
            />
            <StatCard
              label="Média Unitária"
              valor={formatBRL(estatisticas.mediaUnitario)}
              icon={TrendingUp}
              destacado
            />
            <StatCard
              label="Desvio Padrão"
              valor={formatBRL(estatisticas.desvioUnitario)}
              icon={BarChart3}
            />
            <StatCard
              label="Faixa de Valores"
              valor={`${formatBRL(estatisticas.menor)} → ${formatBRL(estatisticas.maior)}`}
              icon={ScaleIconFallback}
              compacto
            />
          </section>
        )}

        {/* GRID DE AMOSTRAS */}
        <section className="bg-white rounded-[3rem] border border-slate-200 shadow-2xl overflow-hidden">

          {/* HEADER DA TABELA */}
          <div className="bg-slate-900 p-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Fingerprint size={20} className="text-amber-400" />
              <h2 className="text-white text-[14px] tracking-widest">
                Memória de Cálculo
              </h2>
              <span className="bg-white/5 text-slate-400 text-[9px] px-3 py-1 rounded-full tracking-widest">
                {amostras.length} item(ns)
              </span>
            </div>

            {amostras.length > 0 && (
              <button
                type="button"
                onClick={limparTudo}
                className="text-slate-400 hover:text-red-500 text-[10px] tracking-widest flex items-center gap-2 transition-colors"
              >
                <Trash2 size={12} /> LIMPAR TUDO
              </button>
            )}
          </div>

          {/* TABELA */}
          {amostras.length === 0 ? (
            <div className="p-20 text-center space-y-4">
              <Calculator size={64} className="mx-auto text-slate-200" />
              <p className="text-slate-400 text-[12px] tracking-widest">
                NENHUMA AMOSTRA REGISTRADA
              </p>
              <p className="text-slate-300 text-[10px] tracking-widest normal-case max-w-md mx-auto">
                Busque amostras do entorno via GPS ou cadastre manualmente
                os comparáveis coletados em campo.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b-2 border-slate-200">
                    <Th>Identificação</Th>
                    <Th align="right">Valor Base</Th>
                    <Th align="right">Área (m²)</Th>
                    <Th align="right">Fator (i)</Th>
                    <Th align="right">Valor Unit.</Th>
                    <Th align="right">V. Homogeneizado</Th>
                    <Th align="center">Data</Th>
                    <Th align="center">Origem</Th>
                    <Th align="center" width="60"></Th>
                  </tr>
                </thead>
                <tbody>
                  {amostras.map((item) => {
                    const fatorPos = item.fator_ajuste >= 0;
                    return (
                      <tr
                        key={item.id}
                        className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="p-4">
                          <div className="space-y-0.5">
                            <p className="text-slate-900 text-[12px] normal-case">
                              {item.identificacao}
                            </p>
                            <p className="text-slate-400 text-[9px] font-mono normal-case">
                              {item.id}
                            </p>
                          </div>
                        </td>
                        <td className="p-4 text-right text-slate-700 text-[12px] font-mono">
                          {formatBRL(item.valor_base)}
                        </td>
                        <td className="p-4 text-right text-slate-700 text-[12px] font-mono">
                          {item.area_referencia.toFixed(2)}
                        </td>
                        <td className="p-4 text-right">
                          <span className={`text-[12px] font-mono ${
                            fatorPos ? 'text-emerald-600' : 'text-red-500'
                          }`}>
                            {formatPct(item.fator_ajuste)}
                          </span>
                        </td>
                        <td className="p-4 text-right text-slate-700 text-[12px] font-mono">
                          {formatBRL(item.valorUnitario)}
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-slate-900 text-[13px] font-mono">
                            {formatBRL(item.valor_homogeneizado)}
                          </span>
                        </td>
                        <td className="p-4 text-center text-slate-500 text-[10px] font-mono normal-case">
                          {item.data_amostra}
                        </td>
                        <td className="p-4 text-center">
                          <span className="bg-slate-100 text-slate-600 text-[9px] px-3 py-1 rounded-full tracking-widest">
                            {item.origem?.tipo || '—'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <button
                            type="button"
                            onClick={() => remover(item.id)}
                            className="w-9 h-9 rounded-full bg-slate-100 text-slate-400 hover:bg-red-500 hover:text-white transition-colors flex items-center justify-center mx-auto"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* FUNDAMENTAÇÃO */}
        <section className="bg-slate-900 rounded-[2.5rem] p-8 border border-white/5">
          <div className="flex items-start gap-4">
            <FileText size={20} className="text-amber-400 shrink-0 mt-1" />
            <div className="space-y-3">
              <p className="text-[10px] text-amber-400 tracking-[0.3em]">
                FUNDAMENTAÇÃO NORMATIVA
              </p>
              <p className="text-slate-300 text-[11px] leading-relaxed tracking-wide normal-case">
                As amostras coletadas são homogeneizadas conforme a fórmula
                <span className="text-amber-400 font-mono mx-1">
                  V_h = V_b × (1 + i)
                </span>
                onde <strong>V_h</strong> é o valor homogeneizado,
                <strong className="mx-1">V_b</strong> é o valor base e
                <strong className="mx-1">i</strong> o fator de ajuste.
                O saneamento estatístico remove outliers além de ±2 desvios-padrão,
                conforme <strong className="text-amber-400">NBR 14653-2:2011, Item 8.2.1.4.2</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="flex flex-wrap justify-between items-center gap-4 pt-8 border-t-2 border-white/5">
          <button
            type="button"
            onClick={onBack}
            disabled={salvando}
            className="px-8 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[11px] tracking-widest hover:bg-white/10 transition-all disabled:opacity-50"
          >
            VOLTAR
          </button>

          <button
            type="button"
            onClick={salvarEAvancar}
            disabled={salvando || amostras.length === 0}
            className="bg-amber-400 text-slate-950 px-10 py-4 rounded-2xl text-[11px] tracking-widest flex items-center gap-3 hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {salvando ? (
              <><Loader2 size={16} className="animate-spin" /> SALVANDO...</>
            ) : (
              <><Save size={16} /> SALVAR E AVANÇAR</>
            )}
          </button>
        </footer>
      </div>

      {/* =========================
          MODAL NOVA AMOSTRA
      ========================= */}
      {mostrarModal && (
        <div className="fixed inset-0 z-[500] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-6">
          <div className="bg-slate-900 w-full max-w-2xl rounded-[3rem] border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">

            {/* HEADER MODAL */}
            <div className="bg-slate-950 p-6 flex justify-between items-center border-b border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-amber-400 rounded-2xl flex items-center justify-center">
                  <Plus size={20} className="text-slate-950" />
                </div>
                <div>
                  <h3 className="text-white text-[16px] tracking-tight leading-none">
                    Nova Amostra de Mercado
                  </h3>
                  <p className="text-amber-400/70 text-[9px] tracking-[0.3em] mt-1">
                    SCHEMA 1.0.0 · NBR 14653-2
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMostrarModal(false)}
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
              >
                <X size={16} />
              </button>
            </div>

            {/* FORM */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">

              {/* Identificação */}
              <Field label="Identificação *">
                <input
                  type="text"
                  value={form.identificacao}
                  onChange={(e) => setForm({ ...form, identificacao: e.target.value })}
                  placeholder="Ex: Amostra 01 — Ref. Centro"
                  className={inputBase}
                />
              </Field>

              {/* Valor + Área */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Valor Base (R$) *">
                  <input
                    type="text"
                    value={form.valor_base}
                    onChange={(e) => setForm({ ...form, valor_base: e.target.value })}
                    placeholder="550000"
                    className={inputBase + ' font-mono'}
                  />
                </Field>
                <Field label="Área (m²) *">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.area_referencia}
                    onChange={(e) => setForm({ ...form, area_referencia: e.target.value })}
                    placeholder="120.00"
                    className={inputBase + ' font-mono'}
                  />
                </Field>
              </div>

              {/* Fator + Data */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fator de Ajuste (i)">
                  <input
                    type="number"
                    step="0.01"
                    min="-1"
                    max="1"
                    value={form.fator_ajuste}
                    onChange={(e) => setForm({ ...form, fator_ajuste: e.target.value })}
                    className={inputBase + ' font-mono'}
                  />
                </Field>
                <Field label="Data da Amostra *">
                  <input
                    type="date"
                    value={form.data_amostra}
                    onChange={(e) => setForm({ ...form, data_amostra: e.target.value })}
                    className={inputBase + ' font-mono'}
                  />
                </Field>
              </div>

              {/* Origem + Fonte */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Origem da Amostra *">
                  <select
                    value={form.origem_tipo}
                    onChange={(e) => setForm({ ...form, origem_tipo: e.target.value })}
                    className={inputBase + ' cursor-pointer'}
                  >
                    {ORIGENS.map(o => (
                      <option key={o} value={o} className="bg-slate-900">{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Fonte da Data *">
                  <select
                    value={form.fonte_data}
                    onChange={(e) => setForm({ ...form, fonte_data: e.target.value })}
                    className={inputBase + ' cursor-pointer'}
                  >
                    {FONTES_DATA.map(f => (
                      <option key={f} value={f} className="bg-slate-900">{f}</option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* URL */}
              <Field label="URL de Referência">
                <input
                  type="url"
                  value={form.origem_url}
                  onChange={(e) => setForm({ ...form, origem_url: e.target.value })}
                  placeholder="https://..."
                  className={inputBase + ' font-mono normal-case'}
                />
              </Field>

              {/* Justificativa — obrigatória se não for CAPTURA_TELA/BANCO_DADOS */}
              <Field
                label={
                  form.origem_tipo === 'CAPTURA_TELA' || form.origem_tipo === 'BANCO_DADOS'
                    ? 'Justificativa (opcional)'
                    : 'Justificativa *'
                }
              >
                <textarea
                  value={form.justificativa}
                  onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                  placeholder="Fundamentação técnica para escolha desta amostra..."
                  rows={3}
                  className={inputBase + ' resize-none normal-case py-3 leading-relaxed'}
                />
              </Field>

              {/* Preview do cálculo */}
              {form.valor_base && form.fator_ajuste && (
                <div className="bg-slate-950 rounded-2xl p-4 border border-amber-500/20 flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 tracking-widest">
                    PRÉVIA DO CÁLCULO
                  </span>
                  <span className="text-amber-400 text-[13px] font-mono">
                    V_h = {formatBRL(parseFloat(String(form.valor_base).replace(/[^\d,-]/g, '').replace(',', '.')) || 0)}
                    {' × '}(1 {parseFloat(form.fator_ajuste) >= 0 ? '+' : ''}
                    {parseFloat(form.fator_ajuste).toFixed(2)}) =
                    {' '}{formatBRL((parseFloat(String(form.valor_base).replace(/[^\d,-]/g, '').replace(',', '.')) || 0) * (1 + (parseFloat(form.fator_ajuste) || 0)))}
                  </span>
                </div>
              )}
            </div>

            {/* FOOTER MODAL */}
            <div className="bg-slate-950 p-6 border-t border-white/5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { resetarForm(); setMostrarModal(false); }}
                className="px-6 py-3 rounded-xl bg-white/5 text-slate-400 text-[11px] tracking-widest hover:bg-white/10 transition-all"
              >
                CANCELAR
              </button>
              <button
                type="button"
                onClick={adicionarManual}
                className="bg-amber-400 text-slate-950 px-6 py-3 rounded-xl text-[11px] tracking-widest flex items-center gap-2 hover:bg-white transition-all"
              >
                <CheckCircle2 size={16} /> ADICIONAR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   SUB-COMPONENTES
========================= */

const inputBase =
  'w-full h-11 bg-slate-950 border border-white/10 rounded-xl px-4 ' +
  'text-white text-[12px] outline-none focus:border-amber-400 ' +
  'transition-all placeholder:text-slate-600';

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="text-[9px] text-slate-500 tracking-[0.3em] block">
        {label}
      </label>
      {children}
    </div>
  );
}

function Th({ children, align = 'left', width }) {
  return (
    <th
      className={`p-4 text-${align} text-[9px] text-slate-500 tracking-[0.2em] font-black`}
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

function StatCard({ label, valor, icon: Icon, destacado, compacto }) {
  return (
    <div className={`bg-slate-900 rounded-[2rem] p-6 border ${
      destacado ? 'border-amber-500/40' : 'border-white/5'
    }`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon size={14} className={destacado ? 'text-amber-400' : 'text-slate-500'} />
        <span className="text-[9px] text-slate-500 tracking-[0.2em]">{label}</span>
      </div>
      <span className={`text-white font-mono ${
        compacto ? 'text-[14px]' : 'text-[22px]'
      }`}>
        {valor}
      </span>
    </div>
  );
}

/* Import correto do ícone Scale */
function ScaleIconFallback(props) {
  return <TrendingDown {...props} />;
}